import type { PortSecurityFormData } from "@features/port-security/schema/portSecurityFormSchema";
import type { AxiosError } from "axios";
import { t } from "i18next";
import type { UseFormSetError } from "react-hook-form";

const mapEmailAddressApiErrorMessage = (
  formField: keyof PortSecurityFormData,
  rawMessage: string,
): string => {
  if (formField !== "emailAddress") {
    return rawMessage;
  }

  const lower = rawMessage.toLowerCase();
  if (lower.includes("well-formed email")) {
    return t("security-clearance.email-well-formed");
  }

  if (/size must be between 0 and \d+/.test(lower)) {
    return t("form-validation.max-length", {
      label: t("security-clearance.email-address"),
      max: 50,
    });
  }

  return rawMessage;
};

const API_FIELD_TO_FORM_FIELD: Partial<Record<string, keyof PortSecurityFormData>> = {
  "residency.phoneNumber": "phoneNo",
  "residency.mobileNumber": "mobileNo",
  "currentSponsor.officeTelephone": "officeTelephone",
  "currentSponsor.officeFax": "officeFax",
  "applicant.emailAddress": "emailAddress",
};

export const applySecurityClearanceApiValidationErrors = (
  error: unknown,
  setError: UseFormSetError<PortSecurityFormData>,
): (keyof PortSecurityFormData)[] => {
  const axiosError = error as AxiosError<{ errors?: Record<string, string> }>;
  const apiErrors = axiosError?.response?.data?.errors;

  if (!apiErrors || typeof apiErrors !== "object") {
    return [];
  }

  const appliedFields: (keyof PortSecurityFormData)[] = [];

  for (const [apiPath, rawMessage] of Object.entries(apiErrors)) {
    const formField = API_FIELD_TO_FORM_FIELD[apiPath];
    if (!formField || !rawMessage) {
      continue;
    }

    setError(formField, {
      type: "server",
      message: mapEmailAddressApiErrorMessage(formField, rawMessage),
    });
    appliedFields.push(formField);
  }

  return appliedFields;
};
